import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import ItemEditDialog from '@/components/ItemEditDialog';
import { money, fmtDate, STATUS_STYLES } from '@/lib/helpers';
import { CustomFieldsView } from '@/components/CustomFieldsView';
import { Loader2, Tag, Package, ArrowLeftRight, ShoppingBag, Undo2, PlusCircle, Pencil } from 'lucide-react';

const EVENT_ICON = { CREATED: PlusCircle, SOLD: ShoppingBag, TRANSFERRED: ArrowLeftRight, RETURNED: Undo2, EDITED: Pencil };
const EVENT_LABEL = { CREATED: 'Added to stock', SOLD: 'Sold', TRANSFERRED: 'Transferred', RETURNED: 'Returned to stock', EDITED: 'Edited' };

export default function GarmentDetail() {
  const { barcode } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, settings, can } = useApp();
  const [editOpen, setEditOpen] = useState(false);
  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const { data: g, isLoading, isError } = useQuery({
    queryKey: ['garment', barcode],
    queryFn: async () => (await api.get(`/garments/${barcode}/history`)).data.data,
    retry: 0,
  });

  if (isLoading) return <Page noNav><TopBar title="Garment" back showStore={false} /><div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Page>;
  if (isError || !g) return <Page noNav><TopBar title="Garment" back showStore={false} /><p className="p-6 text-center text-muted-foreground">No garment found for {barcode}.</p></Page>;

  return (
    <Page noNav>
      <TopBar title="Garment History" back showStore={false} right={
        canManage ? (
          <Button size="sm" variant="secondary" className="h-9 rounded-full" data-testid="garment-edit-button" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4 lg:mr-1.5" /><span className="hidden lg:inline">Edit</span>
          </Button>
        ) : null
      } />
      <div className="px-4 pt-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-semibold">{g.product}</h2>
              <p className="font-mono text-sm text-muted-foreground">{g.barcode}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100'}`}>{g.status}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <Info label="Price" value={money(g.price)} />
            <Info label="Store" value={g.store_name || g.store_id} />
            <Info label="Size" value={g.size || '-'} />
            <Info label="Colour" value={g.colour || '-'} />
            <Info label="Category" value={g.category || '-'} />
            <Info label="Supplier" value={g.supplier_name || '-'} />
            {g.cost != null && (can && can('view_cost')) && <Info label="Cost" value={money(g.cost)} />}
          </div>
          {g.custom && Object.keys(g.custom).length > 0 && (
            <div className="mt-3 border-t border-border pt-3" data-testid="garment-custom">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Additional details</p>
              <CustomFieldsView defs={settings?.custom_fields} values={g.custom} idPrefix="garment-cf" />
            </div>
          )}
        </div>

        <h3 className="mb-2 mt-5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Lifecycle</h3>
        <div className="space-y-3">
          {(g.history || []).slice().reverse().map((h, idx) => {
            const Icon = EVENT_ICON[h.event] || Package;
            return (
              <div key={idx} className="flex gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary"><Icon className="h-4 w-4" /></div>
                <div className="flex-1 rounded-xl border border-border bg-card p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{EVENT_LABEL[h.event] || h.event}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(h.ts)}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {h.invoice_no && `Invoice ${h.invoice_no}`}
                    {h.transfer_no && `Transfer ${h.transfer_no}`}
                    {h.purchase_no && `Purchase ${h.purchase_no}`}
                    {h.salesperson && ` · by ${h.salesperson}`}
                    {h.by && ` · by ${h.by}`}
                    {h.source && ` · ${h.source}`}
                    {Array.isArray(h.fields) && h.fields.length > 0 && ` · changed: ${h.fields.join(', ')}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ItemEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        garment={g}
        onDone={(newBc) => {
          setEditOpen(false);
          if (newBc && newBc !== g.barcode) navigate(`/garment/${newBc}`, { replace: true });
          else qc.invalidateQueries({ queryKey: ['garment', barcode] });
        }}
      />
    </Page>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div>
);
